import { Link } from 'react-router-dom'
import {Target, Heart, ShieldCheck, TrendingUp, ArrowRight} from 'lucide-react'
import SectionHeading from '@/components/SectionHeading'

const values = [
  { icon: Target, title: 'Quality', description: 'Every service is executed to the highest industry standard, measured, and improved continuously.' },
  { icon: Heart, title: 'Consistency', description: 'Identical results across every property, every shift — coast to coast.' },
  { icon: ShieldCheck, title: 'Accountability', description: 'Transparent reporting, clear SLAs, and ownership of every outcome.' },
  { icon: TrendingUp, title: 'Partnership', description: 'We grow with our clients, scaling services as their portfolios evolve.' },
]

function About() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-navy text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-orange-accent blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">About Delta</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              Building trust, <span className="text-orange-accent">one property at a time.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/80 leading-relaxed">
              Delta Facilities Services was founded on a simple principle: property teams deserve one reliable partner for every maintenance need — delivered at scale, without compromise.
            </p>
          </div>
        </div>
      </section>

      {/* COMPANY OVERVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading
              eyebrow="Our Story"
              title="A unified partner in a fragmented industry."
              description="Property managers shouldn't need ten vendors to keep their buildings running. We founded Delta to consolidate excellence — one team, one standard, every service."
            />
            <div className="mt-8 space-y-5 text-navy/85 leading-relaxed">
              <p>
                What began as a regional janitorial firm has grown into a nationwide facility services company serving 500+ properties across Canada. Our expansion has been deliberate — every new market, every new service line, vetted against the same standard of consistency and accountability.
              </p>
              <p>
                Today, Delta operates with the scale of a national provider and the attention of a boutique partner. That balance is our competitive edge, and it's why our clients stay with us year after year.
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://static.lumi.new/c2/c22c2d2154c18275eba76db26dca13c9.png"
              alt="Delta Facilities maintenance technician at work"
              className="rounded-2xl w-full object-cover aspect-[4/5]"
            />
            <div className="absolute -top-6 -left-6 bg-orange-accent text-white rounded-xl p-6 shadow-2xl hidden md:block">
              <div className="font-display text-4xl font-bold">2011</div>
              <div className="text-sm uppercase tracking-wider text-white/90 mt-1">Established</div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VALUES */}
      <section className="py-24 md:py-32 bg-grey-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            centered
            eyebrow="Mission & Values"
            title="What drives us, every day."
            description="Four values anchor every decision, every service, every client interaction."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon
              return (
                <div key={v.title} className="bg-white border border-soft rounded-2xl p-8 hover-lift">
                  <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-navy mb-3">{v.title}</h3>
                  <p className="text-muted text-[15px] leading-relaxed">{v.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="py-24 md:py-32 bg-navy text-white relative overflow-hidden">
        <img
          src="https://static.lumi.new/7a/7aecc72fc43949081279abfe8b66fa93.png"
          alt="Delta Facilities Canadian coverage map"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <SectionHeading
            centered light
            eyebrow="National Reach"
            title="Coast-to-coast coverage, local expertise."
            description="From Vancouver to Halifax, Delta teams are embedded in major Canadian markets — ready to service properties with consistent standards, wherever they're located."
          />
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {['Vancouver', 'Calgary', 'Edmonton', 'Winnipeg', 'Toronto', 'Ottawa', 'Montreal', 'Quebec City', 'Halifax', 'Victoria'].map((city) => (
              <span key={city} className="px-5 py-2 bg-white/10 border border-white/20 rounded-full text-sm font-medium">
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-grey-soft">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-navy leading-tight">
            Let's build something reliable together.
          </h2>
          <p className="mt-5 text-muted text-lg">Join 500+ Canadian properties who trust Delta for their facility maintenance.</p>
          <Link
            to="/quote"
            className="mt-8 inline-flex items-center gap-2 bg-orange-accent hover:bg-orange-dark text-white px-8 py-4 rounded-md font-semibold transition-all group shadow-lg shadow-orange-accent/30"
          >
            Request a Quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default About
